"use client"

import { useIsUserBidder } from "@/app/bids/hooks"
import { BlockchainsMoleculeReatom } from "@/app/blockchains/blockchain-molecule.reatom"
import { useEvent } from "@/app/hooks/use-event"
import { useUserOwnedNft } from "@/app/nft/hooks/use-user-owned-nft"
import { useEvmAcceptBid } from "@/app/sdk/action-molecules/evm/evm-accept-bid-molecule"
import { useEvmAcceptOpenSeaOffer } from "@/app/sdk/action-molecules/evm/evm-accept-opensea-offer-molecule"
import { useEvmCancelByOrder } from "@/app/sdk/action-molecules/evm/evm-cancel-by-order-molecule"
import { useSolanaAcceptItemBid } from "@/app/sdk/action-molecules/solana/solana-accept-item-bid-molecule"
import { useSolanaAcceptMagicEdenOffer } from "@/app/sdk/action-molecules/solana/solana-accept-magic-eden-offer-molecule"
import { useSolanaAcceptMagicEdenPoolBid } from "@/app/sdk/action-molecules/solana/solana-accept-magic-eden-pool-bid-molecule"
import { useSolanaAcceptTensorOffer } from "@/app/sdk/action-molecules/solana/solana-accept-tensor-offer-molecule"
import { useSolanaCancelByOrder } from "@/app/sdk/action-molecules/solana/solana-cancel-by-order-molecule"
import type { ItemOffer } from "@anaconda/core/bid/types"
import {
  isExternalListing,
  isMagicEdenListing,
  isOpenSeaListing,
  isTensorListing,
} from "@anaconda/core/nft/listing-source"
import type { Nft } from "@anaconda/core/nft/types"
import { Trans } from "@lingui/react/macro"
import { isAcceptableRaribleV2Bid } from "@rarible_int/anaconda_sdk/ethereum"
import { Button } from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import { useMolecule } from "bunshi/react"
import { useState } from "react"
import { AcceptOfferDialog } from "./accept-offer-dialog"

interface Props {
  nft: Nft
  offer: ItemOffer
}

/**
 * Picks the row's action component by chain. Each chain's hooks live in their
 * own component so a chain flag that changes after mount — the blockchain list
 * starts with Solana alone when the server prefetch fails, and fills in later —
 * swaps component types rather than changing how many hooks one component
 * calls. Renders nothing until the chain is known.
 */
export const OfferAction = reatomComponent<Props>(({ nft, offer }) => {
  const { reatomIsEvmBlockchain, reatomIsSolanaBlockchain } = useMolecule(
    BlockchainsMoleculeReatom,
  )
  const blockchain = nft.collection.blockchain

  if (reatomIsEvmBlockchain()(blockchain)) {
    return <EvmOfferAction nft={nft} offer={offer} />
  }
  if (reatomIsSolanaBlockchain()(blockchain)) {
    return <SolanaOfferAction nft={nft} offer={offer} />
  }
  return null
}, "OfferAction")

const EvmOfferAction = reatomComponent<Props>(({ nft, offer }) => {
  const isUserBidder = useIsUserBidder(offer.bid)
  const { userOwnedNft } = useUserOwnedNft(nft)
  const blockchain = nft.collection.blockchain
  const { reatomWrappedCurrencyId } = useMolecule(BlockchainsMoleculeReatom)
  const wrappedCurrencyId = reatomWrappedCurrencyId()(blockchain)?.id

  const cancel = useEvmCancelByOrder(offer.bid.order.id, blockchain)
  const accept = useEvmAcceptBid(blockchain)
  const acceptOpenSea = useEvmAcceptOpenSeaOffer(blockchain)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const openConfirm = useEvent(() => setConfirmOpen(true))

  const handleCancel = useEvent(() => cancel.mutate())
  const handleAccept = useEvent(() => {
    // An OpenSea offer is a Seaport order filled on Seaport, not through our
    // exchange.
    if (offer.openSea) {
      acceptOpenSea.mutate({ nft, offer: offer.openSea })
      return
    }
    accept.mutate({ nft, order: offer.bid.order })
  })

  // OpenSea rows first: the maker is the OpenSea bidder and the id is synthetic
  // (`<CHAIN>:os-offer:<hash>`), so an OpenSea bidder who is the connected user
  // must never reach Cancel — the bid is cancelled on OpenSea. Nor Accept:
  // selling into your own offer only pays its fees to get your own NFT back.
  // The fulfilment is only built for selling one ERC721, so an item whose
  // standard is unknown gets no Accept either.
  if (isOpenSeaListing(offer.source)) {
    if (
      !offer.openSea ||
      !userOwnedNft ||
      isUserBidder ||
      nft.collection.type !== "ERC721"
    ) {
      return null
    }

    return (
      <>
        <Button onClick={openConfirm} loading={acceptOpenSea.isPending}>
          <Trans>Accept</Trans>
        </Button>
        <AcceptOfferDialog
          nft={nft}
          offer={offer}
          chain="evm"
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          onConfirm={handleAccept}
        />
      </>
    )
  }

  // Cancel is about who *made* the bid, not who owns the item — check it before
  // the ownership gate, or a bidder who does not own the NFT (the normal case)
  // gets no action at all.
  if (isUserBidder) {
    return (
      <Button
        variant="secondary"
        onClick={handleCancel}
        loading={cancel.isPending}
      >
        <Trans>Cancel</Trans>
      </Button>
    )
  }

  // Everything below is accepting someone else's bid, which only the owner can do.
  if (!userOwnedNft) return null

  // Only bids our ExchangeV2 path can fill, with an ERC721 — the SDK has no
  // ERC1155 accept path, and an item whose standard is unknown could be one.
  // Anything else has no accept action here.
  if (
    !isAcceptableRaribleV2Bid(offer.bid.order, wrappedCurrencyId) ||
    nft.collection.type !== "ERC721"
  ) {
    return null
  }

  return (
    <>
      <Button onClick={openConfirm} loading={accept.isPending}>
        <Trans>Accept</Trans>
      </Button>
      <AcceptOfferDialog
        nft={nft}
        offer={offer}
        chain="evm"
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={handleAccept}
      />
    </>
  )
}, "EvmOfferAction")

const SolanaOfferAction = reatomComponent<Props>(({ nft, offer }) => {
  const isUserBidder = useIsUserBidder(offer.bid)
  const { userOwnedNft } = useUserOwnedNft(nft)

  const cancel = useSolanaCancelByOrder(offer.bid.order.id)
  const accept = useSolanaAcceptItemBid()
  const acceptMagicEden = useSolanaAcceptMagicEdenOffer()
  const acceptMagicEdenPoolBid = useSolanaAcceptMagicEdenPoolBid()
  const acceptTensor = useSolanaAcceptTensorOffer()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const openConfirm = useEvent(() => setConfirmOpen(true))

  const handleCancel = useEvent(() => cancel.mutate())
  const handleAccept = useEvent(() => {
    // A Magic Eden offer is escrowed on their side, so it is filled through
    // their program, not ours — and which program depends on where the offer
    // lives. An auction-house offer names a buyer; a pool bid names a funded
    // pool that will buy any item in the collection, through their AMM.
    if (offer.magicEden?.type === "POOL") {
      acceptMagicEdenPoolBid.mutate({ nft, offer: offer.magicEden })
      return
    }
    if (offer.magicEden?.type === "AUCTION_HOUSE") {
      acceptMagicEden.mutate({ nft, offer: offer.magicEden })
      return
    }
    // Tensor fills its bids and pools through its own programs, one builder
    // for every kind.
    if (offer.tensor) {
      acceptTensor.mutate({ nft, offer: offer.tensor })
      return
    }
    accept.mutate({ itemId: nft.id, order: offer.bid.order })
  })

  // Magic Eden rows first: their maker is the ME buyer, so someone who placed
  // that bid would otherwise match the Cancel branch below and our cancel would
  // be handed the synthetic `SOLANA:me-bid:<mint>` id. A ME bid is cancelled on
  // Magic Eden, so the only action we can offer is Accept, to the owner — and
  // never on their own bid or pool, which would sell the NFT to themselves.
  if (isMagicEdenListing(offer.source)) {
    // Displayable but not acceptable without an auction house — see merge-offers.ts.
    if (!offer.magicEden || !userOwnedNft || isUserBidder) return null

    const isPending =
      offer.magicEden.type === "POOL"
        ? acceptMagicEdenPoolBid.isPending
        : acceptMagicEden.isPending

    return (
      <Button onClick={handleAccept} loading={isPending}>
        <Trans>Accept</Trans>
      </Button>
    )
  }

  // A Tensor bid is cancelled on Tensor, so the only action here is Accept —
  // for the owner, and never on a bid they placed themselves, which Tensor
  // would refuse to fill.
  if (isTensorListing(offer.source)) {
    if (!offer.tensor || !userOwnedNft || isUserBidder) return null

    return (
      <Button onClick={handleAccept} loading={acceptTensor.isPending}>
        <Trans>Accept</Trans>
      </Button>
    )
  }

  // Any other external offer lives on its own marketplace. Until this row has
  // a handler for it, show no action rather than hand its synthetic order to
  // our own cancel or accept.
  if (isExternalListing(offer.source)) return null

  // Cancel is about who *made* the bid, not who owns the item — check it before
  // the ownership gate, or a bidder who does not own the NFT (the normal case)
  // gets no action at all.
  if (isUserBidder) {
    return (
      <Button
        variant="secondary"
        onClick={handleCancel}
        loading={cancel.isPending}
      >
        <Trans>Cancel</Trans>
      </Button>
    )
  }

  // Everything below is accepting someone else's bid, which only the owner can do.
  if (!userOwnedNft) return null

  return (
    <>
      <Button onClick={openConfirm} loading={accept.isPending}>
        <Trans>Accept</Trans>
      </Button>
      <AcceptOfferDialog
        nft={nft}
        offer={offer}
        chain="solana"
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={handleAccept}
      />
    </>
  )
}, "SolanaOfferAction")
